import React, { ElementType, MouseEvent, ReactElement } from 'react'

import { ToggleButton as MUIToggleButton } from '@mui/material'
import { ToggleButtonGroup as MUIToggleButtonGroup } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Icon, { IconName } from 'components_new/atoms/Icon'
import Tooltip from 'components_new/atoms/Tooltip'

interface ToggleButtonProps {
  color?:
    | 'standard'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  component?: ElementType<any>
  disabled?: boolean
  disableBorder?: boolean
  exclusive?: boolean
  fullWidth?: boolean
  items: {
    disabled?: boolean
    iconName?: IconName
    title?: string | ReactElement
    tooltip?: ReactElement | string
    value: any
  }[]
  onChange?: (event: MouseEvent<HTMLElement>, value: any) => void
  orientation?: 'horizontal' | 'vertical'
  size?: 'small' | 'medium' | 'large'
  sx?: object
  value?: any
}

const ToggleButton = React.forwardRef((props: ToggleButtonProps, ref: any) => {
  const {
    color = 'primary',
    disableBorder,
    exclusive = true,
    fullWidth,
    items,
    onChange,
    size,
    sx,
    ...rest
  } = props

  const handleChange = (event: MouseEvent<HTMLElement>, value: any) => {
    if (exclusive && value === null) return
    onChange?.(event, value)
  }

  return (
    <MUIToggleButtonGroup
      color={color}
      exclusive={exclusive}
      fullWidth={fullWidth}
      onChange={handleChange}
      size={size}
      sx={{
        ...(disableBorder
          ? {
              ['& .MuiToggleButtonGroup-grouped']: { borderRadius: 1 }
            }
          : {
              border: '1px solid',
              borderColor: 'divider'
            }),
        ...sx
      }}
      {...rest}
      ref={ref}
    >
      {items.map((item, i) => {
        const content = (
          <Box
            component="span"
            sx={{ alignItems: 'center', display: 'inline-flex' }}
          >
            {item.iconName ? (
              <Icon
                name={item.iconName}
                fontSize={size}
                sx={item.title ? { mr: 1 } : undefined}
              />
            ) : null}
            {item.title}
          </Box>
        )

        return (
          <MUIToggleButton
            disabled={item.disabled}
            key={i}
            sx={{
              border: 0,
              pointerEvents: 'all!important',
              textWrap: 'nowrap',
              '&:hover': {
                bgcolor: item.disabled ? 'transparent' : undefined
              }
            }}
            value={item.value}
          >
            {item.tooltip ? (
              <Tooltip title={item.tooltip}>{content}</Tooltip>
            ) : (
              content
            )}
          </MUIToggleButton>
        )
      })}
    </MUIToggleButtonGroup>
  )
})

ToggleButton.displayName = 'ToggleButton'
export default ToggleButton
